import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Shield, Upload, Check, Clock, FileText, AlertCircle, RefreshCw,
  Eye, Replace, Lock, CheckCircle2,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return dt; }
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocCard({ doc, token, locked, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const uploaded = doc.stato === "ricevuto" && doc.file_id;

  const downloadUrl = uploaded
    ? `${API_URL}/api/ospiti-rete/upload/${token}/documenti/${doc.file_id}/download`
    : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (file.size > 10 * 1024 * 1024) {
      alert("Il file è troppo grande (massimo 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API_URL}/api/ospiti-rete/upload/${token}/documenti/${doc.key}`,
        form
      );
      onUpdated?.(doc.key, {
        stato: "ricevuto",
        file_id: res.data.file_id,
        filename_originale: res.data.filename_originale || file.name,
        size: res.data.size || file.size,
        ext: res.data.ext,
        uploaded_at: new Date().toISOString(),
      });
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nel caricamento del file");
    } finally {
      setUploading(false);
    }
  };

  const isPreviewable = uploaded && doc.ext && [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(doc.ext.toLowerCase());

  return (
    <div className={`p-4 rounded-xl border transition-all ${
      uploaded
        ? locked
          ? "border-emerald-300 bg-emerald-50/80"
          : "border-emerald-200 bg-emerald-50"
        : "border-slate-200 bg-white hover:border-blue-200"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {uploaded ? (
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
              <Check className="w-4 h-4 text-emerald-600" />
            </div>
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-slate-500" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className={`font-medium text-sm ${uploaded ? "text-emerald-800" : "text-slate-800"}`}>
              {doc.label || doc.key}
            </p>
            {uploaded ? (
              <div className="mt-1">
                <p className="text-xs text-emerald-700 truncate" title={doc.filename_originale}>
                  {doc.filename_originale || "Documento caricato"}
                </p>
                {doc.size ? (
                  <p className="text-[11px] text-emerald-600/70">{formatSize(doc.size)}</p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-0.5">Documento non ancora caricato</p>
            )}
          </div>
        </div>

        {/* Azioni */}
        <div className="flex items-center gap-1.5 shrink-0">
          {uploaded && downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              {isPreviewable ? "Apri" : "Scarica"}
            </a>
          )}
          {!locked && (
            uploaded ? (
              <label className={`cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx"
                  onChange={handleFile}
                />
                <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-colors">
                  {uploading ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Replace className="w-3.5 h-3.5" />
                  )}
                  {uploading ? "..." : "Sostituisci"}
                </div>
              </label>
            ) : (
              <label className={`cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx"
                  onChange={handleFile}
                />
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors">
                  {uploading ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {uploading ? "Caricamento..." : "Carica"}
                </div>
              </label>
            )
          )}
          {locked && uploaded && (
            <div className="flex items-center gap-1 px-2 py-1.5 text-slate-400 text-xs">
              <Lock className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>

      {/* Anteprima inline per immagini */}
      {uploaded && isPreviewable && doc.ext?.match(/\.(jpg|jpeg|png|gif|webp)$/i) && downloadUrl && (
        <div className="mt-3 rounded-lg overflow-hidden border border-emerald-100 bg-white">
          <img
            src={downloadUrl}
            alt={doc.filename_originale}
            className="max-h-48 w-full object-contain"
          />
        </div>
      )}
    </div>
  );
}

export default function CaricaDocumentiPortal() {
  const { token } = useParams();
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const fetchDati = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ospiti-rete/upload/${token}`);
      setDati(res.data);
      setErrore(null);
    } catch (err) {
      if (err.response?.status === 410) {
        setErrore("link_scaduto");
      } else if (err.response?.status === 404) {
        setErrore("non_trovato");
      } else {
        setErrore("errore_generico");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDati();
  }, [fetchDati]);

  const handleDocUpdated = (key, updates) => {
    setDati((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documenti_richiesti: prev.documenti_richiesti.map((d) =>
          d.key === key ? { ...d, ...updates } : d
        ),
      };
    });
  };

  const handleConferma = async () => {
    if (!window.confirm(
      "Confermi di aver caricato tutti i documenti correttamente?\n\nDopo la conferma non potrai più modificarli."
    )) {
      return;
    }
    setConfirming(true);
    try {
      const res = await axios.post(`${API_URL}/api/ospiti-rete/upload/${token}/conferma`);
      setDati((prev) => ({
        ...prev,
        completato: true,
        completato_at: res.data.completato_at || new Date().toISOString(),
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Errore durante la conferma");
    } finally {
      setConfirming(false);
    }
  };

  const docs = dati?.documenti_richiesti || [];
  const totalDocs = docs.length;
  const uploadedCount = docs.filter((d) => d.stato === "ricevuto").length;
  const allUploaded = totalDocs > 0 && uploadedCount >= totalDocs;
  const locked = Boolean(dati?.completato);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  if (errore) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">
            {errore === "link_scaduto" ? "Link scaduto" :
             errore === "non_trovato" ? "Link non valido" : "Errore"}
          </h1>
          <p className="text-slate-500 text-sm">
            {errore === "link_scaduto"
              ? "Il link per il caricamento dei documenti è scaduto. Contatta la struttura per richiederne uno nuovo."
              : errore === "non_trovato"
              ? "Il link inserito non è valido o è già stato utilizzato."
              : "Si è verificato un errore. Riprova più tardi o contatta la struttura."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header struttura */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">
              {(dati?.struttura_nome || "A").charAt(0)}
            </span>
          </div>
          <div>
            <h2 className="font-bold text-slate-900">{dati?.struttura_nome || "Struttura"}</h2>
            <p className="text-xs text-slate-500">Richiesta documenti</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {dati?.messaggio && (
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <p className="text-slate-700 text-sm whitespace-pre-line leading-relaxed">{dati.messaggio}</p>
          </div>
        )}

        {dati?.scadenza && !locked && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock className="w-4 h-4" />
            <span>Scadenza link: <strong>{formatDate(dati.scadenza)}</strong></span>
          </div>
        )}

        {/* Stato conferma */}
        {locked ? (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-5 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-bold text-emerald-800 text-lg mb-1">Documentazione confermata</h3>
            <p className="text-emerald-700 text-sm">
              Hai confermato l&apos;invio di {uploadedCount} documenti.
              {dati.completato_at && (
                <> Il {formatDate(dati.completato_at)}.</>
              )}
              {" "}La struttura li ha ricevuti e non è più possibile modificarli.
            </p>
          </div>
        ) : allUploaded ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-emerald-800 text-sm">
                  Tutti i documenti sono stati caricati
                </p>
                <p className="text-emerald-700 text-xs mt-1">
                  Verifica che siano corretti (puoi aprirli e sostituirli se necessario), poi conferma l&apos;invio definitivo.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
            <p className="text-blue-700 text-sm font-medium">
              {uploadedCount} di {totalDocs} documenti caricati
            </p>
            <div className="h-2 bg-blue-100 rounded-full w-32">
              <div
                className="h-2 bg-blue-500 rounded-full transition-all"
                style={{ width: `${totalDocs > 0 ? (uploadedCount / totalDocs) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Documenti */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Documenti richiesti
            {locked && (
              <span className="ml-2 text-xs font-normal text-slate-400 inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> Solo visualizzazione
              </span>
            )}
          </h3>
          <div className="space-y-2">
            {docs.map((doc) => (
              <DocCard
                key={doc.key}
                doc={doc}
                token={token}
                locked={locked}
                onUpdated={handleDocUpdated}
              />
            ))}
          </div>
        </div>

        {/* Pulsante conferma */}
        {!locked && (
          <div className="pt-2 pb-4">
            <Button
              className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              disabled={!allUploaded || confirming}
              onClick={handleConferma}
            >
              {confirming ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-5 h-5" />
              )}
              {confirming ? "Conferma in corso..." : "Conferma invio documenti"}
            </Button>
            {!allUploaded && (
              <p className="text-center text-xs text-slate-400 mt-2">
                Carica tutti i documenti richiesti per abilitare la conferma
              </p>
            )}
            {allUploaded && (
              <p className="text-center text-xs text-slate-400 mt-2">
                Dopo la conferma non potrai più modificare i file caricati
              </p>
            )}
          </div>
        )}

        {/* Privacy */}
        <div className="flex items-start gap-2 text-xs text-slate-400 pb-8">
          <Shield className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            I documenti vengono trasmessi in modo sicuro e protetto. Sono accessibili solo al personale
            autorizzato della struttura e vengono trattati in conformità al GDPR (Reg. UE 2016/679).
          </p>
        </div>
      </div>
    </div>
  );
}
